import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { User, LogOut, Settings, ChevronDown } from 'lucide-react';
import { signOut } from '../../lib/supabase/auth';
import { signOut as firebaseSignOut } from '../../lib/firebase/auth';

export default function UserMenu() {
  const { user, firebaseUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSignOut = async () => {
    const [supabaseResult, firebaseResult] = await Promise.all([
      signOut(),
      firebaseSignOut()
    ]);

    if (supabaseResult.error) {
      console.error('Error signing out from Supabase:', supabaseResult.error);
    }
    if (firebaseResult.error) {
      console.error('Error signing out from Firebase:', firebaseResult.error);
    }

    setIsOpen(false);
  };

  const currentUser = user || firebaseUser;

  if (currentUser) {
    const displayName = user?.user_metadata?.name || firebaseUser?.displayName || currentUser.email?.split('@')[0] || 'User';

    return (
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          onMouseEnter={() => setIsOpen(true)}
          className={`
            flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200
            ${isOpen
              ? 'bg-green-50 text-green-700'
              : 'text-gray-900 hover:bg-gray-100 hover:text-green-600'
            }
          `}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <div className="flex items-center justify-center h-8 w-8 rounded-full bg-green-600 text-white font-medium">
            {displayName.charAt(0).toUpperCase()}
          </div>
          <span className="font-medium hidden sm:block">{displayName}</span>
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isOpen && (
          <div
            className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg py-2 z-50 border border-gray-200 animate-fadeIn"
            onMouseLeave={() => setIsOpen(false)}
          >
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-900">{displayName}</p>
              <p className="text-xs text-gray-500 truncate">{currentUser.email}</p>
            </div>

            <Link
              to="/profile"
              onClick={() => setIsOpen(false)}
              className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Settings className="h-4 w-4 mr-3 text-gray-400" />
              Profile Settings
            </Link>

            <button
              onClick={handleSignOut}
              className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-t border-gray-100"
            >
              <LogOut className="h-4 w-4 mr-3 text-gray-400" />
              Sign Out
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-1 sm:space-x-4">
      <Link
        to="/login"
        className="text-gray-900 hover:text-green-600 px-2 py-1 sm:px-4 sm:py-2 text-sm sm:text-base rounded-lg hover:bg-gray-100 transition-all"
      >
        Sign In
      </Link>
      <Link
        to="/signup"
        className="bg-green-600 text-white px-2 py-1 sm:px-4 sm:py-2 text-sm sm:text-base rounded-lg hover:bg-green-700 transition-all shadow-sm hover:shadow-md"
      >
        Sign Up
      </Link>
    </div>
  );
}
